/* EscalationsBell — admin notification bell rendering /api/admin/escalations.
 *
 * Layer 4 of the production workflow. The bell polls every 30s for the
 * count + items list. Clicking opens a popover with:
 *   - One row per active escalation
 *   - Severity chip (rose for critical, amber for normal)
 *   - "Open case" link to /admin/cases/{id}
 *   - "Acknowledge" button (POST /escalate-acknowledge — admin/coe_lead only)
 *   - `minutes_open` badge with red colour when > 15 min SLA breach
 *
 * Uses native <details>/<summary> for the popover so we don't pull in a
 * Radix Popover here — the dropdown lives in a sticky header and Popover
 * portals into the body which can cause z-index headaches at viewport
 * edges. The native popover degrades gracefully on touch.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, Siren, X, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";


const POLL_INTERVAL_MS = 30000;


export default function EscalationsBell() {
    const [data, setData] = useState({ total: 0, items: [], sla_target_minutes: 15 });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // case_id being acknowledged

    const load = useCallback(() => {
        api.get("/admin/escalations")
            .then((r) => setData(r.data))
            .catch(() => setData({ total: 0, items: [], sla_target_minutes: 15 }))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
        const interval = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [load]);

    const acknowledge = async (caseId) => {
        if (busy) return;
        setBusy(caseId);
        try {
            await api.post(`/production/cases/${caseId}/escalate-acknowledge`);
            toast.success("Escalation cleared");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to acknowledge");
        } finally { setBusy(null); }
    };

    const total = data.total || 0;
    const hasBreaches = (data.items || []).some((i) => i.sla_breached);

    return (
        <details className="relative" data-testid="admin-escalations-bell">
            <summary
                className="list-none cursor-pointer relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-secondary transition-colors select-none"
                title={total ? `${total} active escalation${total === 1 ? "" : "s"}` : "No escalations"}
                data-testid="admin-escalations-bell-toggle"
            >
                <Bell size={18} weight={total ? "fill" : "regular"} className={total ? "text-primary" : "text-muted-foreground"} />
                {total > 0 && (
                    <span
                        className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${hasBreaches ? "bg-rose-600" : "bg-amber-500"} text-white`}
                        data-testid="admin-escalations-count"
                    >
                        {total > 99 ? "99+" : total}
                    </span>
                )}
            </summary>

            <div className="absolute right-0 top-full mt-2 z-50 w-96 max-h-[70vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl">
                <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Siren size={14} weight="fill" className="text-rose-600" />
                        <h3 className="font-display font-bold text-sm">
                            Production escalations {total > 0 && <span className="text-muted-foreground font-normal">· {total}</span>}
                        </h3>
                    </div>
                    {total > 0 && hasBreaches && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded">
                            {(data.items || []).filter((i) => i.sla_breached).length} past SLA
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="px-4 py-6 text-center text-muted-foreground text-xs">Loading…</div>
                ) : total === 0 ? (
                    <div className="px-4 py-8 text-center" data-testid="admin-escalations-empty">
                        <CheckCircle size={28} weight="duotone" className="mx-auto text-emerald-600 mb-2" />
                        <div className="text-sm font-semibold">All clear</div>
                        <div className="text-xs text-muted-foreground mt-0.5">No production escalations to triage.</div>
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {(data.items || []).map((it) => (
                            <li key={it.id} className="px-4 py-3" data-testid={`admin-escalation-${it.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                                            <span className="font-mono text-[10px] text-muted-foreground font-bold">{it.code || it.id?.slice(0, 8)}</span>
                                            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                                                it.production_escalation_severity === "critical"
                                                    ? "bg-rose-100 text-rose-800 border border-rose-200"
                                                    : "bg-amber-100 text-amber-800 border border-amber-200"
                                            }`}>
                                                {it.production_escalation_severity || "normal"}
                                            </span>
                                            <span
                                                className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                                                    it.sla_breached
                                                        ? "bg-rose-100 text-rose-800 border border-rose-200"
                                                        : "bg-secondary text-muted-foreground"
                                                }`}
                                                title={`SLA target: ${data.sla_target_minutes || 15} min`}
                                            >
                                                {Math.round(it.minutes_open)}m open
                                            </span>
                                        </div>
                                        <div className="text-sm font-semibold text-foreground truncate">
                                            {it.patient_name || "Patient"}
                                            <span className="text-muted-foreground font-normal text-xs"> · {it.doctor_name || "—"}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                            {it.production_escalation_reason}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground mt-1">
                                            Stage: {it.stage_label} · Raised by {it.production_escalation_by_name}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <Link
                                        to={`/admin/cases/${it.id}`}
                                        className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                        data-testid={`admin-escalation-open-${it.id}`}
                                    >
                                        Open case <ArrowRight size={10} />
                                    </Link>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => acknowledge(it.id)}
                                        disabled={busy === it.id}
                                        className="ml-auto h-7 text-[11px] rounded-full"
                                        data-testid={`admin-escalation-ack-${it.id}`}
                                    >
                                        <CheckCircle size={11} className="mr-1" weight="fill" />
                                        {busy === it.id ? "Clearing…" : "Acknowledge"}
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="px-4 py-2 border-t border-border bg-secondary/20 text-[10px] text-muted-foreground flex items-center justify-between">
                    <span>SLA: {data.sla_target_minutes || 15} min to acknowledge</span>
                    <Link to="/admin/cases?filter=escalated" className="text-primary hover:underline font-semibold">
                        View all
                    </Link>
                </div>
            </div>
        </details>
    );
}
